import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { STATUS_LABEL, TAN, trimOrNull } from './equipment-shared.js';

const STATUS_OPTIONS = Object.keys(STATUS_LABEL);

const Field = ({ label, hint, children, full = false }) => (
  <div class={full ? 'col-span-2' : ''}>
    <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
      {label}
      {hint && <span class="ml-1 font-medium text-text-muted">· {hint}</span>}
    </p>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
  />
);

const blank = {
  name: '', categoryId: '', model: '', status: 'active', location: '', notes: '',
  ipAddress: '', macAddress: '', osVersion: '', peripherals: '',
  serialNumber: '', assetId: '', purchaseDate: '', supplierContactId: '',
};

/** Add/edit non-printer equipment (laminators, workstations, label printers, etc). */
export const EquipmentEditModal = ({ open, onClose, onSaved, equipment }) => {
  const editing = !!equipment;
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: categories, refetch: refetchCategories } = useApi(open ? '/equipment/categories' : null);
  const { data: contacts } = useApi(open ? '/contacts' : null);

  const [form, setForm] = useState(blank);
  const [newCategory, setNewCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setNewCategory('');
    setForm(editing ? {
      ...blank,
      ...equipment,
      categoryId: equipment.categoryId ?? '',
      model: equipment.model ?? '',
      location: equipment.location ?? '',
      notes: equipment.notes ?? '',
      ipAddress: equipment.ipAddress ?? '',
      macAddress: equipment.macAddress ?? '',
      osVersion: equipment.osVersion ?? '',
      peripherals: equipment.peripherals ?? '',
      serialNumber: equipment.serialNumber ?? '',
      assetId: equipment.assetId ?? '',
      purchaseDate: equipment.purchaseDate ?? '',
      supplierContactId: equipment.supplierContactId ?? '',
    } : blank);
  }, [open, editing, equipment]);

  if (!open) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.currentTarget.value }));

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    try {
      const created = await api.post('/equipment/categories', { name });
      setNewCategory('');
      await refetchCategories();
      setForm((f) => ({ ...f, categoryId: created.id }));
    } catch (err) {
      setError(err);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name:               form.name.trim(),
        categoryId:         form.categoryId === '' ? null : Number(form.categoryId),
        model:              trimOrNull(form.model),
        status:             form.status,
        location:           trimOrNull(form.location),
        notes:              trimOrNull(form.notes),
        ipAddress:          trimOrNull(form.ipAddress),
        macAddress:         trimOrNull(form.macAddress),
        osVersion:          trimOrNull(form.osVersion),
        peripherals:        trimOrNull(form.peripherals),
        serialNumber:       trimOrNull(form.serialNumber),
        assetId:            trimOrNull(form.assetId),
        purchaseDate:       trimOrNull(form.purchaseDate),
        supplierContactId:  form.supplierContactId === '' ? null : Number(form.supplierContactId),
      };
      const saved = editing
        ? await api.patch(`/equipment/${equipment.id}`, payload)
        : await api.post('/equipment', payload);
      onSaved?.(saved);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete equipment "${equipment.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/equipment/${equipment.id}`);
      onSaved?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-xl overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: TAN }}>
            {editing ? 'Edit equipment' : 'Add equipment'}
          </p>
          <p class="m-0 font-serif text-lg font-medium text-text-primary">
            {editing ? equipment.name : 'New machine'}
          </p>
        </div>

        <div class="space-y-5 p-5">
          <div class="grid grid-cols-2 gap-3">
            <Field label="Name"><Input value={form.name} onInput={set('name')} placeholder="e.g. Laminator 1" autoFocus /></Field>
            <Field label="Category" hint="or add a new one below">
              <select
                value={form.categoryId}
                onChange={set('categoryId')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="">No category</option>
                {(categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>

            <Field label="Model" hint="optional"><Input value={form.model} onInput={set('model')} placeholder="e.g. GBC Foton 30" /></Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={set('status')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </Field>
          </div>

          <div class="flex items-end gap-2">
            <div class="flex-1">
              <Field label="New category" hint="e.g. laminator, label printer">
                <Input
                  value={newCategory}
                  onInput={(e) => setNewCategory(e.currentTarget.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                  placeholder="Category name"
                />
              </Field>
            </div>
            <Button variant="secondary" onClick={addCategory} disabled={!newCategory.trim()}>Add</Button>
          </div>

          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Network</p>
            <div class="grid grid-cols-2 gap-3">
              <Field label="IP address" hint="optional"><Input value={form.ipAddress} onInput={set('ipAddress')} placeholder="192.168.1.40" /></Field>
              <Field label="MAC address" hint="optional"><Input value={form.macAddress} onInput={set('macAddress')} placeholder="AA:BB:CC:DD:EE:FF" /></Field>
              <Field label="OS version" hint="optional" full><Input value={form.osVersion} onInput={set('osVersion')} placeholder="e.g. Windows 11 Pro" /></Field>
            </div>
          </div>

          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Equipment</p>
            <div class="grid grid-cols-2 gap-3">
              <Field label="Serial number" hint="optional"><Input value={form.serialNumber} onInput={set('serialNumber')} /></Field>
              <Field label="Asset ID" hint="optional"><Input value={form.assetId} onInput={set('assetId')} placeholder="Your asset tag" /></Field>
              <Field label="Location" hint="optional"><Input value={form.location} onInput={set('location')} placeholder="e.g. Finishing room" /></Field>
              <Field label="Acquired" hint="YYYY-MM-DD"><Input value={form.purchaseDate} onInput={set('purchaseDate')} placeholder="2014-03-12" /></Field>
              <Field label="Supplier" hint="optional" full>
                <select
                  value={form.supplierContactId}
                  onChange={set('supplierContactId')}
                  class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                >
                  <option value="">None</option>
                  {(contacts ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Notes</p>
            <Field label="Peripherals" hint="optional">
              <textarea
                rows={2}
                value={form.peripherals}
                onInput={set('peripherals')}
                placeholder="e.g. attached scanner, second monitor"
                class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </Field>
            <div class="mt-3">
              <Field label="Notes" hint="optional">
                <textarea
                  rows={2}
                  value={form.notes}
                  onInput={set('notes')}
                  class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </Field>
            </div>
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          {editing && isAdmin && (
            <Button variant="ghost" onClick={remove} disabled={busy} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
          <div class="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={busy || !form.name.trim()}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add equipment'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
